import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useUser } from '@src/state/user/hooks';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { event as TaggingEvent } from '@uhg-abyss/web/tools/event';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Loading } from '@src/common/Loading';
import { Button } from '@src/common/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { useTinRegistration } from '@src/state/tinRegistration';

const Space = styled('div', {
  marginTop: '30px',
});

const AlignRemoveIcon = styled('span', {
  marginLeft: '16px',
  cursor: 'pointer',
  display: 'flex',
  marginTop: '2px',
});

const InputDescription = styled('p', {
  fontSize: '14px',
  textAlign: 'left',
  lineHeight: '18px',
  fontWeight: 600,
  padding: '10px',
  marginLeft: '8px',
  fontFamily: '$primary',
});

const ExpiryDate = styled('div', {
  fontSize: '14px',
});

const ExpiryWrapper = styled('div', {
  padding: '5px',
});

export const SavedRegistration = ({
  tinDraftResult,
  setTinDraft,
  searchFilter,
  getFormatedDate,
  onYes,
  setSessionDraft,
}) => {
  const { deleteSession, disableSaveAsDraft } = useTinRegistration.actions();
  const userInfo = useUser.state();
  const [savedCount, setSavedCount] = useState([]);
  const { deleteSessionSaga } = useTinRegistration.state();

  const handleDeleteSession = (row, index) => {
    const { billingTin, uuid } = row;
    deleteSession({ billingTin, index }, (res) => {
      setTinDraft((prevTinDraft) => {
        const updatedTin = [...prevTinDraft];
        updatedTin.splice(index, 1);
        const formattedTinResults = getFormatedDate(updatedTin);
        if (formattedTinResults && formattedTinResults.length) {
          setSessionDraft(true);
        } else {
          setSessionDraft(false);
        }
        return formattedTinResults;
      });
    });
    TaggingEvent('DELETE_REGISTRATION', { uuid, billingTin });
    const eventData = {
      action: 'Remove',
    };
    TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
  };

  const handleEditSession = (row) => {
    if (row) {
      disableSaveAsDraft(false);
      onYes(row);
    }
    const eventData = {
      action: 'Edit',
    };
    TaggingEvent('EAPM_TRANSACTION_CONTINUE', eventData);
  };

  const getColumns = () => {
    return [
      {
        Header: 'Tax ID Numner(TIN)',
        accessor: 'billingTin',
      },
      {
        Header: 'Name',
        accessor: 'w9Details.name',
      },
      {
        Header: 'Application Start Date',
        accessor: 'startDate',
      },
      {
        Header: 'Expiration Date',
        accessor: 'expiryDate',
        Cell: (rowData) => {
          const { row } = rowData;
          const { original } = row;
          const { expiryDate, expiryOn } = original;
          return (
            <ExpiryWrapper id="expiry-wrapper">
              <ExpiryDate id="dashboard-savedDraftText">
                {expiryDate} <br />
              </ExpiryDate>
              {expiryOn} days
            </ExpiryWrapper>
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: (rowData) => {
          const { row } = rowData;
          const { original, index } = row;
          return (
            <div>
              <AlignRemoveIcon id="editsavedicon">
                <IconSymbol icon="edit" size="18px" />
                <Button
                  variant="tertiary"
                  id="edit-save-button"
                  size="size400"
                  onClick={() => {
                    return handleEditSession(original);
                  }}
                >
                  Edit
                </Button>
              </AlignRemoveIcon>

              <AlignRemoveIcon id="removesavedIcon">
                <IconSymbol icon="delete" size="18px" />
                <Button
                  variant="tertiary"
                  id="remove-save-button"
                  size="size400"
                  onClick={() => {
                    handleDeleteSession(original, index);
                  }}
                >
                  Remove
                </Button>
              </AlignRemoveIcon>
            </div>
          );
        },
      },
    ];
  };

  const columns = getColumns();

  const dataTableProps = useDataTable({
    initialColumns: columns,
    showPagination: false,
    showColumnSort: false,
  });

  useEffect(() => {
    const pageName = 'epvsecure:home';
    const billingTin = '';
    const siteSectionLevel = {
      siteSectionLevel1: 'home',
      siteSectionLevel2: '',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    TaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });
    setSavedCount(tinDraftResult.length);

    const data = tinDraftResult.reduce((acc, row) => {
      if (
        columns.some((column) => {
          return !searchFilter || searchFilter(row[column.accessor]);
        })
      ) {
        acc.push({
          billingTin: row.billingTin,
          'w9Details.name': row.w9Details.name,
          startDate: row.startDate,
          expiryDate: row.expiryDate,
          expiryOn: row.expiryOn,
          actions: row.actions,
          ...row,
        });
      }
      return acc;
    }, []);

    dataTableProps.setColumns(columns, true);
    dataTableProps.setData(data, true);
  }, [tinDraftResult]);

  return (
    <React.Fragment>
      <Loading isLoading={deleteSessionSaga.isLoading}>
        <Space id="saved-registration">
          <Card
            header={
              <strong id="saved-registration-label">
                Saved Registration ({savedCount}/5)
              </strong>
            }
          >
            <InputDescription id="dashboard-savedDraftText">
              These TIN registrations are currently in progress and have yet to
              be submitted.
            </InputDescription>
            <DataTable hideTitleHeader tableState={dataTableProps} />
          </Card>
        </Space>
      </Loading>
    </React.Fragment>
  );
};

SavedRegistration.propTypes = {
  tinDraftResult: PropTypes.func.isRequired,
  setTinDraft: PropTypes.func.isRequired,
  searchFilter: PropTypes.func,
  getFormatedDate: PropTypes.func.isRequired,
  onYes: PropTypes.func.isRequired,
  setSessionDraft: PropTypes.func.isRequired,
};

SavedRegistration.defaultProps = {
  searchFilter: null,
};
